import { Formik, FormikHelpers } from 'formik';
import React, { useEffect, useState } from 'react';
import { SelectItem } from '../../../../common/ui-components/multi-select/multi-select.component';
import { TeamModel } from '../../../teams/models/team-model';
import { UserModel } from '../../../users/models/user-model';
import { getAccountTeams, getAccountUsers, updateAccount } from '../../account-service';
import AccountEditor, {
    EditAccountFormValue,
    EditAccountProps,
    formSchema
} from '../../components/edit-account.component';
import { AssignedTeam, AssignedUser } from '../../models/account-create-model';
import FetchAccount from '../../resolver/fetch-account.component';
import { useNavigate, useParams } from 'react-router';

const mapSelectItemsToUsers = (
    items: SelectItem<UserModel>[],
    isAccountManager: boolean
): AssignedUser[] => items.map(u => u.value).map(u => ({ ...u, isAccountManager }));

const mapTeamToSelectItem = (team: AssignedTeam): SelectItem<AssignedTeam> => ({
    value: team,
    label: team.name
});

const mapUserToSelectItem = (user: UserModel): SelectItem<UserModel> => ({
    value: user,
    label: `${user.firstName} ${user.lastName}`
});

const mapTeamToAssignedTeam = (team: TeamModel): AssignedTeam => ({
    id: team.id,
    name: team.name,
    sport: team.sport,
    league: team.league
});

const EditAccountView = (props: EditAccountProps) => {
    const navigate = useNavigate();

    const onEdit = (
        formValue: EditAccountFormValue,
        { setSubmitting }: FormikHelpers<EditAccountFormValue>
    ) => {
        const { teams, users, accountManagers, ...fields } = formValue;
        updateAccount({
            id: props.account.id,
            teams: teams.map(t => t.value),
            users: [
                ...mapSelectItemsToUsers(accountManagers, true),
                ...mapSelectItemsToUsers(users, false)
            ],
            ...fields
        })
            .then(() => navigate(-1))
            .finally(() => setSubmitting(false));
    };

    return (
        <Formik
            initialValues={{
                name: props.account.name,
                users: (props.users ?? [])
                    .filter(u => !u.isAccountManager)
                    .map(mapUserToSelectItem),
                accountManagers: (props.users ?? [])
                    .filter(u => u.isAccountManager)
                    .map(mapUserToSelectItem),
                teams: (props.teams ?? []).map(mapTeamToSelectItem),
                validUntil:
                    props.account.validUntil == null
                        ? ('' as any)
                        : new Date(props.account.validUntil),
                validFrom:
                    props.account.validFrom == null
                        ? ('' as any)
                        : new Date(props.account.validFrom),
                teamQuota: props.account.teamQuota ?? ('' as any),
                surveyQuota: props.account.surveyQuota ?? ('' as any)
            }}
            validationSchema={formSchema}
            enableReinitialize={true}
            onSubmit={onEdit}
        >
            {formikProps => <AccountEditor {...props} {...formikProps} />}
        </Formik>
    );
};

const EditAccount = () => {
    const { accountId } = useParams();
    const [teams, setTeams] = useState<AssignedTeam[]>([]);
    const [users, setUsers] = useState<UserModel[]>([]);

    useEffect(() => {
        getAccountTeams(accountId)
            .then(t => t.map(mapTeamToAssignedTeam))
            .then(setTeams);
        getAccountUsers(accountId).then(setUsers);
    }, []);

    return (
        <FetchAccount>
            {account => <EditAccountView account={account} users={users} teams={teams} />}
        </FetchAccount>
    );
};

export default EditAccount;
